import { useState, useEffect } from 'react';
import { useGetBalance } from '@/query/queries/cash/useGetBalance.js';
import { safeDecimal } from '@/helpers/number.js';

export const useAllocationBalance = ({
  allocatedGroupA = 0,
  allocatedGroupB = 0,
  confirmedAllocationGroupA = 0,
  confirmedAllocationGroupB = 0,
  confirmedBonusAmount = 0,
}) => {
  const { data: balance } = useGetBalance();

  const initialAvailableCashAmount = safeDecimal(balance?.available);
  const initialBonusAmount = safeDecimal(balance?.available_bonus_amount);

  const [availableCashAmount, setAvailableCashAmount] = useState(
    initialAvailableCashAmount.toNumber(),
  );
  const [bonusAmount, setBonusAmount] = useState(initialBonusAmount.toNumber());

  useEffect(() => {
    // Convert allocations to Decimal for precise calculations
    const allocatedA = safeDecimal(allocatedGroupA);
    const allocatedB = safeDecimal(allocatedGroupB);

    // Calculate the new bonus and cash amounts
    let newBonusAmount = initialBonusAmount.add(
      safeDecimal(confirmedBonusAmount),
    );
    let newAvailableCashAmount = initialAvailableCashAmount
      .add(safeDecimal(confirmedAllocationGroupA))
      .add(safeDecimal(confirmedAllocationGroupB))
      .sub(safeDecimal(confirmedBonusAmount));

    // Use bonuses for Group A first
    if (allocatedA.lte(newBonusAmount)) {
      newBonusAmount = newBonusAmount.minus(allocatedA);
    } else {
      const remainingA = allocatedA.minus(newBonusAmount);
      newBonusAmount = safeDecimal(0);
      newAvailableCashAmount = newAvailableCashAmount.minus(remainingA);
    }

    // Deduct Group B entirely from available cash
    newAvailableCashAmount = newAvailableCashAmount.minus(allocatedB);

    // Ensure no negative values
    newBonusAmount = Math.max(newBonusAmount.toNumber(), 0);
    newAvailableCashAmount = Math.max(newAvailableCashAmount.toNumber(), 0);

    // Update state
    setBonusAmount(newBonusAmount);
    setAvailableCashAmount(newAvailableCashAmount);
  }, [
    allocatedGroupA,
    allocatedGroupB,
    initialAvailableCashAmount,
    initialBonusAmount,
    confirmedAllocationGroupA,
    confirmedAllocationGroupB,
    confirmedBonusAmount,
  ]);

  const total = safeDecimal(availableCashAmount).plus(safeDecimal(bonusAmount));

  return {
    availableCashAmount: availableCashAmount,
    availableBonusAmount: bonusAmount,
    total: total.toNumber(),
  };
};
